// SynthesisContext — makes the resolved synthesis payload available to every tab as the SAME named
// slices they used to import from './fixtures'. The shell resolves the payload (live via
// useSynthesisData, else the bundled fixture) and provides it here, so a tab reads `useSynth()`
// instead of a hardcoded fixture — real data flows with zero change to a tab's body. Default value
// is the fixture, so anything rendered outside a provider still works.

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { SynthesisPayload } from './types';
import { synthesis as fixture } from './fixtures';

const SynthesisCtx = createContext<SynthesisPayload>(fixture);
// The current project id, threaded so tabs that own editable, viz-owned state (e.g. Interview
// Coverage's roster) can persist it per project. Undefined outside a project → those tabs stay
// read-only against the fixture.
const ProjectIdCtx = createContext<string | undefined>(undefined);
// Lets a tab swap the whole payload in place after a server-side edit (e.g. regenerating Insights &
// Opportunities, or a Mind Map merge recompute) without a page refresh. Undefined outside the shell.
const ApplyCtx = createContext<((p: SynthesisPayload) => void) | undefined>(undefined);
type SynthNav = {
  active: string;
  setActive: (id: string) => void;
  quoteFilter: number | 'all';
  setQuoteFilter: (v: number | 'all') => void;
  expandedThemeRank: number | null;
  setExpandedThemeRank: (n: number | null) => void;
};
const noop = () => {};
const SynthNavCtx = createContext<SynthNav>({
  active: 'exec-summary',
  setActive: noop,
  quoteFilter: 'all',
  setQuoteFilter: noop,
  expandedThemeRank: null,
  setExpandedThemeRank: noop,
});

export function SynthesisProvider(
  { payload, projectId, applyPayload, nav, children }:
    { payload?: SynthesisPayload; projectId?: string; applyPayload?: (p: SynthesisPayload) => void; nav?: SynthNav; children: ReactNode },
) {
  const navValue = nav || {
    active: 'exec-summary',
    setActive: noop,
    quoteFilter: 'all' as const,
    setQuoteFilter: noop,
    expandedThemeRank: null,
    setExpandedThemeRank: noop,
  };
  return (
    <SynthNavCtx.Provider value={navValue}>
      <SynthesisCtx.Provider value={payload || fixture}>
        <ProjectIdCtx.Provider value={projectId}>
          <ApplyCtx.Provider value={applyPayload}>{children}</ApplyCtx.Provider>
        </ProjectIdCtx.Provider>
      </SynthesisCtx.Provider>
    </SynthNavCtx.Provider>
  );
}

/** The current project id (undefined when the module renders outside a project). */
export function useSynthProjectId(): string | undefined {
  return useContext(ProjectIdCtx);
}

/** Swap the rendered payload in place after a server-side edit (undefined outside the shell). */
export function useSynthApply(): ((p: SynthesisPayload) => void) | undefined {
  return useContext(ApplyCtx);
}

export function useSynthNav(): SynthNav {
  return useContext(SynthNavCtx);
}

/** The synthesis payload as the named slices the tabs consume (same names as ./fixtures). */
export function useSynth() {
  const p = useContext(SynthesisCtx);
  return useMemo(() => ({
    synthesis: p,
    engagement: p.engagement,
    sources: p.sources,
    themes: p.themes,
    quotes: p.quotes,
    insights: p.insights,
    opportunities: p.opportunities,
    journey: p.journey,
    mindmap: p.mindmap,
    workflowArchitecture: p.workflow_architecture,
    firstPrinciples: p.firstPrinciples,
    coverage: p.coverage,
    meta: p.meta,
    totalQuotes: p.quotes.length,
  }), [p]);
}
